import { Card } from '@ipfabric/ui-components/Card/Card';
import { date } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { useApiTableDeletionHelper } from 'components/ApiTable/helpers/useApiTableDeletionHelper';
import { ApiTablePageView } from 'components/ApiTablePage/ApiTablePageView';
import { useApiTablePage } from 'components/ApiTablePage/useApiTablePage';
import { Button } from 'components/Button/Button';
import { CopyTextDialog } from 'components/CopyTextDialog/CopyTextDialog';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { useLocale } from 'containers/App/LocaleContext';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { toDate } from 'date-fns';
import { useMemo, useState } from 'react';
import type { FC } from 'react';
import { useApi } from 'utils/request/useApi';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';
import { ApiTokenDialog, type EditedApiToken } from './ApiTokenDialog';
import { ApiTokensHeader } from './ApiTokensHeader';
import type { RowData } from './ApiTokensState';
import { getEndOfDayInMillis } from './helpers/getEndOfDayInMillis';
import { useDialog } from './useDialog';

export const apiTokensEndpoint = '/tables/settings/api-tokens';

const adminOnlyColumns = ['username', 'userId'];

export const ApiTokens: FC = () => {
  const msg = useLocale();
  const confirmDialog = useSimpleConfirmDialog();
  const [editedEntity, setEditedEntity] = useState<EditedApiToken | null>(null);
  const [savedToken, setSavedToken] = useState<string | null>(null);
  const api = useApi();

  const ctxProps = useGlobalStateContextSelectors({
    timezone: prop('user', 'timezone'),
    dateFormat: prop('dateFormat'),
    dateTimeFormat: prop('dateTimeFormat'),
    admin: prop('auth', 'admin'),
  });
  const [createDialog, openCreateDialog] = useDialog({
    component: ApiTokenDialog,
    dialogProps: {
      type: 'create',
      dateFormat: ctxProps.dateFormat,
      getApiTableModel: () => apiTokensTablePage.apiTableModel,
      setSavedToken,
    } as any,
  });

  const [editDialog, openEditDialog] = useDialog({
    component: ApiTokenDialog,
    dialogProps: {
      type: 'edit',
      initialValues: editedEntity
        ? {
            description: editedEntity.description,
            expirationEnabled: editedEntity.expires !== null,
            expires: editedEntity.expires
              ? toDate(new Date(editedEntity.expires))
              : undefined,
            roleIds: editedEntity.roleIds,
            id: editedEntity.id,
          }
        : undefined,
      dateFormat: ctxProps.dateFormat,
      getApiTableModel: () => apiTokensTablePage.apiTableModel,
    } as any,
  });

  const deletionHelper = useApiTableDeletionHelper({
    confirmDialog,
    confirmDialogMessages: {
      header: 'API token will be deleted',
      subHeader: 'Do you really want to delete selected API token?',
    },
    getApiTableModel: () => apiTokensTablePage.apiTableModel,
    deleteOne: (id: string) => {
      return api.request({
        url: `/api-tokens/${id}`,
        method: 'DELETE',
      });
    },
  });

  const formatDate = (format: string, value: string | null) =>
    value !== null &&
    date.formatUtcTimestampInTimeZone(
      new Date(value),
      ctxProps.timezone,
      format,
    );

  const endpoint = useTableEndpoint({
    apiEndpoint: apiTokensEndpoint,
    alternative: (origEndpoint) => ({
      id: origEndpoint.id,
      columns: [
        'username',
        'description',
        createFrontendOnlyColumn({
          key: 'expires',
          disabledSort: true,
          title: 'Expiration',
          webDependencies: ['expires'],
          render: (row) => {
            const isExpired =
              row.expires && Date.now() > getEndOfDayInMillis(row.expires);
            return (
              <div
                className={classNames(
                  'ipf-table__cell-content',
                  isExpired && 'ipf-table__severity-badge',
                  isExpired && 'ipf-table__severity-badge--severity-30',
                )}
              >
                {row.expires === null
                  ? 'Never'
                  : formatDate(ctxProps.dateFormat, row.expires)}
              </div>
            );
          },
        }),
        createFrontendOnlyColumn({
          key: 'lastUsed',
          disabledSort: true,
          title: 'Last Used',
          webDependencies: ['lastUsed'],
          render: (row) => (
            <div className='ipf-table__cell-content'>
              {row.lastUsed === null
                ? 'Never'
                : formatDate(ctxProps.dateTimeFormat, row.lastUsed)}
            </div>
          ),
        }),
        '...',
        createFrontendOnlyColumn({
          key: 'createdAt',
          disabledSort: true,
          title: 'Created At',
          webDependencies: ['createdAt'],
          render: (row) => (
            <div className='ipf-table__cell-content'>
              {row.createdAt
                ? formatDate(ctxProps.dateTimeFormat, row.createdAt)
                : ''}
            </div>
          ),
        }),
        createFrontendOnlyColumn({
          key: 'action',
          disabledSort: true,
          title: 'Action',
          webDependencies: ['id', 'roleIds', 'description', 'expires'],
          render: (rowData: RowData) => {
            return (
              <>
                <Button
                  variant='tertiary'
                  icon='fa-pen'
                  onClick={() => {
                    setEditedEntity({
                      ...rowData,
                      roleIds: rowData.roleIds ?? [],
                    });
                    openEditDialog();
                  }}
                  size='xxs'
                  type='button'
                />
                {deletionHelper.renderDeleteButton(rowData.id)}
              </>
            );
          },
        }),
      ],
    }),
  });

  // `username` and `userId` identify token owners; only useful to admins, who
  // see everyone's tokens.
  const gatedEndpoint = useMemo(() => {
    if (ctxProps.admin) {
      return endpoint;
    }
    return {
      ...endpoint,
      columns: endpoint.columns.filter(
        (c) => !adminOnlyColumns.includes(c.key),
      ),
    };
  }, [endpoint, ctxProps.admin]);

  const apiTokensTablePage = useApiTablePage({
    endpoint: gatedEndpoint,
    additionalApiTableProps: {
      loading: deletionHelper.loading,
    },
  });

  return (
    <>
      <CopyTextDialog
        text={savedToken}
        onClose={() => {
          setSavedToken(null);
        }}
        msg={msg.settings.apiTokens.savedTokenDialog}
      />
      <div className='flex flex-col flex-1 gap m'>
        <Card className='p'>
          <ApiTokensHeader
            hasTokenSet={apiTokensTablePage?.apiTableModel?.data.length > 0}
            msg={msg}
            openDialog={openCreateDialog}
          />
        </Card>
        <ApiTablePageView model={apiTokensTablePage} />
        {confirmDialog.render()}
        {createDialog}
        {editDialog}
      </div>
    </>
  );
};
